import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

type TimerMode = 'work' | 'break' | 'idle';

interface TimerContextType {
  isActive: boolean;
  timeRemaining: number;
  mode: TimerMode;
  cyclesCompleted: number;
  workDuration: number;
  breakDuration: number;
  isPomodoroMode: boolean;
  selectedSubject: string;
  isFullscreen: boolean;
  startTimer: (duration: number, subject?: string) => void;
  startPomodoro: (workMins?: number, breakMins?: number, subject?: string) => void;
  resumeTimer: () => void;
  pauseTimer: () => void;
  resetTimer: () => void;
  skipToNext: () => void;
  setSelectedSubject: (subject: string) => void;
  setFullscreen: (isFullscreen: boolean) => void;
  setWorkDuration: (mins: number) => void;
  setBreakDuration: (mins: number) => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};

interface TimerProviderProps {
  children: ReactNode;
}

const TIMER_STORAGE_KEY = 'novari_timer_state';

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {
  // Load initial state from localStorage
  const getInitialState = () => {
    const saved = localStorage.getItem(TIMER_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          timeRemaining: parsed.timeRemaining || 0,
          targetEndTime: parsed.targetEndTime || null,
          mode: parsed.mode || 'idle',
          cyclesCompleted: parsed.cyclesCompleted || 0,
          workDuration: parsed.workDuration || 25,
          breakDuration: parsed.breakDuration || 5,
          isPomodoroMode: parsed.isPomodoroMode || false,
          selectedSubject: parsed.selectedSubject || '',
        };
      } catch (e) {
        console.error('Failed to parse timer state', e);
      }
    }
    return {
      timeRemaining: 0,
      targetEndTime: null,
      mode: 'idle',
      cyclesCompleted: 0,
      workDuration: 25,
      breakDuration: 5,
      isPomodoroMode: false,
      selectedSubject: '',
    };
  };

  const initialState = getInitialState();
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(initialState.timeRemaining);
  const [targetEndTime, setTargetEndTime] = useState<number | null>(initialState.targetEndTime);
  const [mode, setMode] = useState<TimerMode>(initialState.mode);
  const [cyclesCompleted, setCyclesCompleted] = useState(initialState.cyclesCompleted);
  const [workDuration, setWorkDuration] = useState(initialState.workDuration);
  const [breakDuration, setBreakDuration] = useState(initialState.breakDuration);
  const [isPomodoroMode, setIsPomodoroMode] = useState(initialState.isPomodoroMode);
  const [selectedSubject, setSelectedSubject] = useState(initialState.selectedSubject);
  const [isFullscreen, setFullscreen] = useState(false);

  // Persist state to localStorage
  useEffect(() => {
    const state = {
      timeRemaining,
      targetEndTime,
      mode,
      cyclesCompleted,
      workDuration,
      breakDuration,
      isPomodoroMode,
      selectedSubject,
    };
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(state));
  }, [timeRemaining, targetEndTime, mode, cyclesCompleted, workDuration, breakDuration, isPomodoroMode, selectedSubject]);

  const switchToBreak = useCallback(() => {
    setMode('break');
    const duration = breakDuration * 60;
    setTimeRemaining(duration);
    setTargetEndTime(Date.now() + duration * 1000);
    setIsActive(true);
  }, [breakDuration]);

  const switchToWork = useCallback(() => {
    setMode('work');
    const duration = workDuration * 60;
    setTimeRemaining(duration);
    setTargetEndTime(Date.now() + duration * 1000);
    setIsActive(true);
    setCyclesCompleted((prev: number) => prev + 1);
  }, [workDuration]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && targetEndTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((targetEndTime - now) / 1000));
        
        setTimeRemaining(remaining);

        if (remaining === 0) {
          // Timer finished
          if (isPomodoroMode) {
            if (mode === 'work') {
              switchToBreak();
            } else if (mode === 'break') {
              switchToWork();
            }
          } else {
            setIsActive(false);
            setMode('idle');
            setTargetEndTime(null);
          }
          if (interval) clearInterval(interval);
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, targetEndTime, isPomodoroMode, mode, switchToBreak, switchToWork]);

  // Simple timer (no pomodoro)
  const startTimer = (duration: number, subject?: string) => {
    if (subject) setSelectedSubject(subject);
    setIsPomodoroMode(false);
    setWorkDuration(duration);
    const seconds = duration * 60;
    setTimeRemaining(seconds);
    setTargetEndTime(Date.now() + seconds * 1000);
    setMode('work');
    setIsActive(true);
  };

  // Pomodoro timer with work/break cycles
  const startPomodoro = (workMins: number = 25, breakMins: number = 5, subject?: string) => {
    if (subject) setSelectedSubject(subject);
    setIsPomodoroMode(true);
    setWorkDuration(workMins);
    setBreakDuration(breakMins);
    const seconds = workMins * 60;
    setTimeRemaining(seconds);
    setTargetEndTime(Date.now() + seconds * 1000);
    setMode('work');
    setCyclesCompleted(0);
    setIsActive(true);
  };

  const resumeTimer = () => {
    if (timeRemaining > 0) {
      setTargetEndTime(Date.now() + timeRemaining * 1000);
      setIsActive(true);
    }
  };

  const pauseTimer = () => {
    setIsActive(false);
    setTargetEndTime(null);
  };

  const resetTimer = () => {
    setIsActive(false);
    setTimeRemaining(workDuration * 60);
    setTargetEndTime(null);
    setMode('idle');
    setCyclesCompleted(0);
    setIsPomodoroMode(false);
  };

  // Sync timeRemaining with duration when idle
  useEffect(() => {
    if (mode === 'idle' && !isActive) {
      setTimeRemaining(workDuration * 60);
    }
  }, [workDuration, mode, isActive]);

  const skipToNext = () => {
    if (!isPomodoroMode) return;
    if (mode === 'work') {
      switchToBreak();
    } else {
      switchToWork();
    }
  };

  return (
    <TimerContext.Provider value={{
      isActive,
      timeRemaining,
      mode,
      cyclesCompleted,
      workDuration,
      breakDuration,
      isPomodoroMode,
      selectedSubject,
      isFullscreen,
      startTimer,
      startPomodoro,
      resumeTimer,
      pauseTimer,
      resetTimer,
      skipToNext,
      setSelectedSubject,
      setFullscreen,
      setWorkDuration,
      setBreakDuration,
    }}>
      {children}
    </TimerContext.Provider>
  );
};